import AppContext from 'App/AppContext';
import { IPLink, IPSpinner, IPText } from 'baseComponents';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { globalStackTokensGapSmall } from 'globalStyles';
import { AICriterionOutcomeResult } from 'models/auditCriteriaGeneric';
import { AICriterionOutcome } from 'models/auditCriteriaGenericSets';
import ResourceLink from 'models/resourceLink';
import { useContext, useEffect, useState } from 'react';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { getOutcomeSourceDisplayName } from './automationNames';
import { apiGetCriterionOutcome } from 'services/Api/auditCriteriaService';
import { apiGetLinksForId } from 'services/Api/linkService';
import { apiRequest } from 'services/Auth/authConfig';
import { toLocaleDateShort } from 'utils/datetime';
import { getOutcomeRunProcess, openAssessmentAutomationRun } from './assessmentAutomationStart';

// An AI-proposed answer's tooltip mounts on every hover: an outcome read by id is read once per page load. An outcome
// does not change once written; its status does, and the details do not show it. The evidence links are kept once read:
// a text-only read (textOnly) leaves them undefined, so the next full read fetches them.
const outcomesById = new Map<number, { outcome: AICriterionOutcome; links?: ResourceLink[] }>();

interface IAICriterionOutcomeDetailsProps {
  // the outcome when the host has it (the criteria view), else its id (an AI-proposed answer on a form)
  outcome?: AICriterionOutcome;
  outcomeId?: number;
  // the result line and the summary only, without the evidence and the two links: a host that shows the details in a
  // tooltip (the task form), where a link cannot be reached because the tooltip closes. The evidence is not read then.
  textOnly?: boolean;
}

/**
 * An AI outcome of a criterion, opened (PLAN-075, spec 5.7): the result, where it came from and when, its summary, the
 * documents it found as evidence and the task it was applied to. Reads the outcome by id when only the id is known, and
 * the evidence documents through the link read, which leaves out what the user may not read. A process run's outcome
 * opens that run in the panel on the item (PLAN-073). With textOnly it shows the result line and the summary only.
 */
const AICriterionOutcomeDetails = (props: IAICriterionOutcomeDetailsProps) => {
  const { t } = useTranslation(['criteria', 'translation']);
  const appContext = useContext(AppContext);

  const [outcome, setOutcome] = useState<AICriterionOutcome | undefined>(props.outcome);
  const [links, setLinks] = useState<ResourceLink[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const cached = props.outcome ? undefined : outcomesById.get(props.outcomeId ?? 0);
      if (cached && (props.textOnly || cached.links !== undefined)) {
        setOutcome(cached.outcome);
        setLinks(cached.links ?? []);
        setIsLoading(false);

        return;
      }

      try {
        setIsLoading(true);
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const loaded =
          props.outcome ??
          cached?.outcome ??
          (props.outcomeId ? await apiGetCriterionOutcome(props.outcomeId, accessToken) : undefined);
        const evidence = props.textOnly
          ? undefined
          : loaded && loaded.evidenceLinkIds.length > 0
          ? await apiGetLinksForId(accessToken, loaded.evidenceLinkIds, appContext.globalDataCache)
          : [];
        if (loaded && !props.outcome)
          outcomesById.set(loaded.aiCriterionOutcomeId, { outcome: loaded, links: evidence ?? cached?.links });
        if (cancelled) return;
        setOutcome(loaded);
        setLinks(evidence ?? []);
      } catch {
        // the details are an aid beside the answer: a failed read says the details are not available and leaves the
        // answer as it is
        if (!cancelled) setOutcome(undefined);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.outcome, props.outcomeId, props.textOnly]);

  const getResultText = (result: AICriterionOutcomeResult): string => {
    switch (result) {
      case AICriterionOutcomeResult.Pass:
        return t('criteria:Generic.Outcome.Pass');
      case AICriterionOutcomeResult.Fail:
        return t('criteria:Generic.Outcome.Fail');
      default:
        return t('criteria:Generic.Outcome.Inconclusive');
    }
  };

  // a document opens at its web address, which the link resolves (a SharePoint item or a provider's file)
  const openLink = async (link: ResourceLink) => {
    try {
      window.open(await link.getWebURL(appContext), '_blank', 'noopener,noreferrer');
    } catch (err) {
      appContext.setError(err);
    }
  };

  if (isLoading) return <IPSpinner size={IPSpinnerSize.small} />;
  if (!outcome) return <IPText>{t('criteria:Generic.Outcome.Unavailable')}</IPText>;

  const runProcess = props.textOnly ? undefined : getOutcomeRunProcess(outcome.sourceKind, outcome.sourceRef);

  return (
    <IPStack tokens={globalStackTokensGapSmall} styles={{ root: { maxWidth: 420 } }}>
      <IPText block styles={{ root: { fontWeight: 600 } }}>
        {t('criteria:Generic.Outcome.Available', {
          result: getResultText(outcome.proposedResult),
          source: getOutcomeSourceDisplayName(
            outcome.sourceKind,
            outcome.sourceRef,
            outcome.sourceName,
            t as TFunction<string[]>,
          ),
          date: toLocaleDateShort(outcome.created),
        })}
      </IPText>
      {outcome.summary && <IPText block>{outcome.summary}</IPText>}
      {!props.textOnly && links.length > 0 && (
        <IPStack tokens={globalStackTokensGapSmall}>
          <IPText block variant="small">
            {t('criteria:Generic.Outcome.Evidence')}
          </IPText>
          {links.map((link) => (
            <IPLink key={link.linkId} onClick={() => void openLink(link)}>
              {link.linkName}
            </IPLink>
          ))}
        </IPStack>
      )}
      {runProcess !== undefined && (
        <IPLink
          title={t('criteria:Generic.Outcome.OpenResultInfo')}
          onClick={() => openAssessmentAutomationRun(appContext, runProcess, outcome.entityType, outcome.entityId)}
        >
          {t('criteria:Generic.Outcome.OpenResult')}
        </IPLink>
      )}
      {!props.textOnly && outcome.appliedToTaskId !== undefined && (
        <IPLink href={`/tasks/${outcome.appliedToTaskId}`} target="_blank" rel="noopener noreferrer">
          {t('criteria:Generic.Outcome.OpenTask')}
        </IPLink>
      )}
    </IPStack>
  );
};

export default AICriterionOutcomeDetails;
